/* ── Dev messages: what went wrong in this tab ────────────────────────
   The screenshot popup's "Scan App" reads this. From the moment the app
   loads it keeps, in memory, every console error and every API call that
   failed -- whether or not anyone has the browser's console open -- so a
   user can copy the lot into Google Chat or an email for the developers.

   Frontend only: nothing is sent anywhere, and it is gone on reload.

   It hooks in four places, once:
     console.error              -- what the app itself reports
     window "error"             -- uncaught exceptions
     window "unhandledrejection"-- promises nobody caught
     XMLHttpRequest and fetch   -- axios uses XHR; a status of 400+ or no
                                   answer at all counts as a failed call
   Each list keeps the latest MAX entries, so a long session cannot grow
   it without bound. Request bodies and headers are never recorded -- they
   carry passwords and tokens; the response is kept only as a short excerpt.
   ------------------------------------------------------------------ */

const MAX = 200;
const MESSAGE_CAP = 1500;
const BODY_CAP = 500;

const consoleErrors = [];
const apiErrors = [];
const since = new Date();

const now = () => new Date().toISOString();
const page = () => (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '');
const cap = (text, n) => {
  const s = String(text ?? '');
  return s.length > n ? `${s.slice(0, n)}…` : s;
};
const push = (list, entry) => {
  list.push(entry);
  if (list.length > MAX) list.splice(0, list.length - MAX);
};

// One console argument as text: an Error by its message and first stack
// lines, an object as JSON, anything else as itself.
const describe = (value) => {
  if (value instanceof Error) {
    const stack = String(value.stack || '').split('\n').slice(0, 4).join('\n');
    return stack || `${value.name}: ${value.message}`;
  }
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  }
  return String(value);
};

const recordConsole = (kind, parts) => {
  push(consoleErrors, {
    at: now(),
    kind,
    page: page(),
    message: cap(parts.map(describe).join(' '), MESSAGE_CAP),
  });
};

const recordApi = ({ method, url, status, statusText, body }) => {
  push(apiErrors, {
    at: now(),
    method: String(method || 'GET').toUpperCase(),
    url: String(url || ''),
    status: status || 0,
    statusText: statusText || (status ? '' : 'No response (network error, CORS or timeout)'),
    response: cap(body, BODY_CAP),
    page: page(),
  });
};

const install = () => {
  if (typeof window === 'undefined' || window.__devMessagesInstalled) return;
  window.__devMessagesInstalled = true;

  // console.error -- still prints exactly as before.
  const originalError = console.error;
  console.error = function devMessagesError(...args) {
    try {
      recordConsole('console', args);
    } catch (_) {
      /* recording must never break logging */
    }
    return originalError.apply(this, args);
  };

  window.addEventListener('error', (event) => {
    // A failed <img>/<script> fires this too, with no message; name it.
    const target = event.target;
    if (target && target !== window && (target.src || target.href)) {
      recordConsole('resource', [`Failed to load ${target.tagName?.toLowerCase() || 'resource'}: ${target.src || target.href}`]);
      return;
    }
    recordConsole('error', [event.error || event.message || 'Unknown error']);
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    recordConsole('promise', ['Unhandled promise rejection:', event.reason]);
  });

  // XMLHttpRequest (axios). The method and URL are noted on open; the
  // outcome is read once the request ends, however it ends.
  const XHR = window.XMLHttpRequest;
  if (XHR?.prototype) {
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;
    XHR.prototype.open = function devMessagesOpen(method, url, ...rest) {
      this.__dm = { method, url };
      return open.call(this, method, url, ...rest);
    };
    XHR.prototype.send = function devMessagesSend(...args) {
      const meta = this.__dm;
      if (meta) {
        this.addEventListener('loadend', () => {
          try {
            if (this.status === 0 || this.status >= 400) {
              let body = '';
              if (this.responseType === '' || this.responseType === 'text') body = this.responseText;
              else if (this.responseType === 'json' && this.response) body = JSON.stringify(this.response);
              recordApi({ ...meta, status: this.status, statusText: this.statusText, body });
            }
          } catch (_) {
            /* never break the request */
          }
        });
      }
      return send.apply(this, args);
    };
  }

  // fetch. The response is cloned so the caller still gets to read it.
  if (typeof window.fetch === 'function') {
    const originalFetch = window.fetch;
    window.fetch = async function devMessagesFetch(input, init) {
      const method = init?.method || input?.method || 'GET';
      const url = typeof input === 'string' ? input : input?.url || String(input);
      try {
        const res = await originalFetch.call(this, input, init);
        if (res.status >= 400) {
          res
            .clone()
            .text()
            .then((body) => recordApi({ method, url, status: res.status, statusText: res.statusText, body }))
            .catch(() => recordApi({ method, url, status: res.status, statusText: res.statusText }));
        }
        return res;
      } catch (error) {
        recordApi({ method, url, status: 0, body: error?.message });
        throw error;
      }
    };
  }
};

install();

/** Everything recorded so far, oldest first. */
export const getDevMessages = () => ({
  since: since.toISOString(),
  scannedAt: now(),
  page: page(),
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  consoleErrors: consoleErrors.slice(),
  apiErrors: apiErrors.slice(),
});

/** Start over -- after a report has been sent, say. */
export const clearDevMessages = () => {
  consoleErrors.length = 0;
  apiErrors.length = 0;
};

/* The report as plain text, ready to paste into Google Chat or an email.
   Who and where first, then the failed calls (usually the cause), then
   the console. */
export const formatDevMessages = (scan, { by, note } = {}) => {
  const lines = [];
  lines.push('LMS app scan');
  if (by) lines.push(`By: ${by}`);
  lines.push(`Page: ${scan.page}`);
  lines.push(`Scanned: ${new Date(scan.scannedAt).toLocaleString()}`);
  lines.push(`Recording since: ${new Date(scan.since).toLocaleString()}`);
  lines.push(`Browser: ${scan.userAgent}`);
  if (note) lines.push(`Note: ${note}`);
  lines.push('');

  lines.push(`Failed API calls (${scan.apiErrors.length})`);
  if (!scan.apiErrors.length) lines.push('  none');
  scan.apiErrors.forEach((e, i) => {
    lines.push(`${i + 1}. ${e.method} ${e.url} -> ${e.status || 'no response'}${e.statusText ? ` ${e.statusText}` : ''}`);
    lines.push(`   at ${new Date(e.at).toLocaleTimeString()} on ${e.page}`);
    if (e.response) lines.push(`   response: ${e.response.replace(/\s+/g, ' ')}`);
  });
  lines.push('');

  lines.push(`Console errors (${scan.consoleErrors.length})`);
  if (!scan.consoleErrors.length) lines.push('  none');
  scan.consoleErrors.forEach((e, i) => {
    lines.push(`${i + 1}. [${e.kind}] ${new Date(e.at).toLocaleTimeString()} on ${e.page}`);
    e.message.split('\n').forEach((l) => lines.push(`   ${l}`));
  });

  return lines.join('\n');
};
